import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckIcon, DownloadIcon, MailCheckIcon, ChevronRightIcon } from 'lucide-react';
import { Container, Kicker, Callout } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Field, inputClass, ConsentCheckbox } from '../components/ui/Field';
import { getResource } from '../data/resources';
import { SECTORS } from '../data/services';
import { submitLead, requestDownloadUrl } from '../lib/leads';
import { track } from '../lib/analytics';
import { NotFound } from './NotFound';
export function ResourcePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const resource = getResource(slug ?? '');
  const [email, setEmail] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [organisation, setOrganisation] = React.useState('');
  const [role, setRole] = React.useState('');
  const [sector, setSector] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [downloadUrl, setDownloadUrl] = React.useState<string | null>(null);
  if (!resource) return <NotFound data-id="element-1239" data-mp="3l2xkjztnskiq" />;
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await submitLead({
      email,
      firstName,
      organisation,
      role,
      sector,
      consent,
      source: `resource:${resource.slug}`
    });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'Something went wrong. Please try again.');
      return;
    }
    const download = await requestDownloadUrl(resource.fileName, {
      kind: 'resource',
      id: resource.slug
    });
    if (!download.url) {
      setError(download.error ?? 'We could not prepare your download. Please try again.');
      return;
    }
    setDownloadUrl(download.url);
    track('ebook_download', {
      resource: resource.slug,
      sector
    });
  };
  return <>
      <section className="surface-dark" data-id="element-1240" data-mp="2ulva79u3alyx">
        <Container className="py-12 sm:py-16" data-id="element-1241" data-mp="2umwxccodqyx0">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-1242" data-mp="2unojkww37kam">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-1243" data-mp="2fya7vi51gqs2">
              <li data-id="element-1244" data-mp="2kx3mxezz4puc"><Link to="/" className="hover:text-white" data-id="element-1245" data-mp="2kx3ldakbshs8">Home</Link></li>
              <li aria-hidden="true" data-id="element-1246" data-mp="38lwdsx1qo7i3"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1247" data-mp="38mo7u3fotx69" /></li>
              <li data-id="element-1248" data-mp="3dkpnsfhthgdi"><Link to="/resources" className="hover:text-white" data-id="element-1249" data-mp="3dkpsgssri4ju">Resources</Link></li>
              <li aria-hidden="true" data-id="element-1250" data-mp="3did9qdf6jq5q"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1251" data-mp="2zl84ugr7cxc2" /></li>
              <li aria-current="page" className="truncate text-white/80" data-id="element-1252" data-mp="2zn15nff4wnc5">{resource.title}</li>
            </ol>
          </nav>
          <Kicker data-id="element-1253" data-mp="1o5t3l0rynjb5">{resource.format}</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[32px] leading-tight text-white sm:text-[44px]" data-id="element-1254" data-mp="1o503ginibs1z">
            {resource.title}
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-1255" data-mp="1knvin9qldzsp">{resource.summary}</p>
        </Container>
      </section>

      <Container className="py-14 sm:py-20" data-id="element-1256" data-mp="1fpk9l184ftbj">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]" data-id="element-1257" data-mp="1foizp02udcve">
          <div data-id="element-1258" data-mp="1ul09vnzaolgd">
            <img src={resource.cover} alt={resource.coverAlt} className="w-full max-w-[380px] rounded-md" data-id="element-1259" data-mp="1uljfq6bo7je1" />
            <h2 className="mt-10 font-display text-[26px] text-ink" data-id="element-1260" data-mp="11wunyxwi8qey">What is inside</h2>
            <ul className="mt-5 space-y-3" data-id="element-1261" data-mp="wyjbsgiqm3tk">
              {resource.whatIsInside.map(item => <li key={item} className="flex gap-3 text-[16px] text-body" data-id="element-1262" data-mp="1bmxemrg5lsa4">
                  <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-1263" data-mp="1bp0ee373yzs1" />
                  {item}
                </li>)}
            </ul>
            <h2 className="mt-10 font-display text-[26px] text-ink" data-id="element-1264" data-mp="j5bm4ue7viei">Who it is for</h2>
            <p className="mt-4 max-w-prose text-[16px] text-body" data-id="element-1265" data-mp="e70ace4d2xtn">{resource.forWho}</p>
            <p className="mt-4 text-small text-muted" data-id="element-1266" data-mp="3dmu0yvybl7ws">
              {resource.pages} pages. Delivered as a PDF through a secure link that expires after fifteen minutes.
            </p>
          </div>

          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-1267" data-mp="3mlfrpedy3t8n">
            <div className="rounded-md border border-line bg-mist p-6 sm:p-8" data-id="element-1268" data-mp="3rd78wyb7omm4">
              {downloadUrl ? <div data-id="element-1269" data-mp="7fl45kwpm2h">
                  <div className="flex items-center gap-2 text-gold" data-id="element-1270" data-mp="5urqjabw967">
                    <MailCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-1271" data-mp="55qvqij100lr" />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-1272" data-mp="57tw9wht1c4q">Ready</span>
                  </div>
                  <h2 className="mt-4 font-display text-[24px] leading-tight text-ink" data-id="element-1273" data-mp="e775qp5xi0pg">
                    Your download is ready
                  </h2>
                  <p className="mt-3 text-[15px] text-body" data-id="element-1274" data-mp="2b0s5hii8qpzd">
                    We have also emailed the link to {email}, so you can come back to it. The link expires after
                    fifteen minutes and can be reissued from that email.
                  </p>
                  <a href={downloadUrl} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-gold px-5 py-3.5 text-[15px] font-medium text-ink transition-colors hover:bg-[#c19f2c]" data-id="element-1275" data-mp="23mrk6e1huwmw">
                    <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1276" data-mp="2rd58btgykbc7" />
                    Download the PDF
                  </a>
                  <p className="mt-4 break-all text-small text-muted" data-id="element-1277" data-mp="2mfls4o0p9ohm">Secure link: {downloadUrl}</p>
                  <div className="mt-6 border-t border-line pt-6" data-id="element-1278" data-mp="2hdwcsw6g2onq">
                    <Button to="/contact" variant="secondary" className="w-full" data-id="element-1279" data-mp="2de5fsaqbrcar">
                      Book a discovery call
                    </Button>
                  </div>
                </div> : <form onSubmit={onSubmit} className="space-y-5" data-id="element-1280" data-mp="11xe2pdnkoev3">
                  <div data-id="element-1281" data-mp="13fvxulxvegyb">
                    <h2 className="font-display text-[24px] leading-tight text-ink" data-id="element-1282" data-mp="13fcs03lhvj0n">Get your copy</h2>
                    <p className="mt-2 text-[15px] text-body" data-id="element-1283" data-mp="18ez1q3246h4p">
                      Five fields. We use them to send the file and to tailor what we send you next.
                    </p>
                  </div>

                  <Field id="res-email" label="Work email" required error={error ?? undefined} data-id="element-1284" data-mp="yf8803ldo65o">
                    <input id="res-email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="you@organisation.org" className={inputClass} data-id="element-1285" data-mp="kocw0ifl18xv" />
                  </Field>
                  <Field id="res-name" label="First name" required data-id="element-1286" data-mp="1q0of14bk74e">
                    <input id="res-name" type="text" required value={firstName} onChange={event => setFirstName(event.target.value)} className={inputClass} data-id="element-1287" data-mp="5rugil0g82sy" />
                  </Field>
                  <Field id="res-org" label="Organisation" required data-id="element-1288" data-mp="3sap4mn3qt2q8">
                    <input id="res-org" type="text" required value={organisation} onChange={event => setOrganisation(event.target.value)} className={inputClass} data-id="element-1289" data-mp="3nat5p29wk1hj" />
                  </Field>
                  <Field id="res-role" label="Your role" required data-id="element-1290" data-mp="eu80zxoazuk8">
                    <input id="res-role" type="text" required value={role} onChange={event => setRole(event.target.value)} className={inputClass} data-id="element-1291" data-mp="asw89ja4ut7t" />
                  </Field>
                  <Field id="res-sector" label="Sector" required data-id="element-1292" data-mp="1r5i8nekbk0sh">
                    <select id="res-sector" required value={sector} onChange={event => setSector(event.target.value)} className={inputClass} data-id="element-1293" data-mp="1v7c1j0o9w0i3">
                      <option value="" data-id="element-1294" data-mp="17iarj8zskhap">Please choose</option>
                      {SECTORS.map(item => <option key={item} value={item} data-id="element-1295" data-mp="1ch43uy3ce29a">
                          {item}
                        </option>)}
                    </select>
                  </Field>

                  <ConsentCheckbox id="res-consent" checked={consent} onChange={setConsent} data-id="element-1296" data-mp="1m6x6h9lib71e">
                    I am happy for Cavalla Solution to email me about this download and related insights. See our{' '}
                    <Link to="/legal/privacy" className="underline underline-offset-2" data-id="element-1297" data-mp="tq4a7jkwk49o">
                      privacy notice
                    </Link>
                    .
                  </ConsentCheckbox>

                  <Button type="submit" size="lg" disabled={submitting} className="w-full" data-id="element-1298" data-mp="h9bnqtrfpyu9">
                    {submitting ? 'Preparing your file' : 'Send me the download'}
                  </Button>
                </form>}
            </div>

            <Callout className="mt-6" data-id="element-1299" data-mp="7cgt4lya8il9">
              Files are held in private storage. Every download is issued through a signed link generated for that
              request, so the file path is never exposed.
            </Callout>
          </div>
        </div>
      </Container>
    </>;
}